import { Link } from 'react-router-dom';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import DimensionLine from '@/components/DimensionLine';

export const meta = {
  path: '/waterloo-region-commercial-real-estate-market-q2-2026',
  title: 'Waterloo Region Commercial Real Estate Market — Q2 2026',
  description: 'Q2 2026 Waterloo Region commercial real estate intelligence: industrial availability, office vacancy, absorption, construction and employment context, with source methodology.',
};

const metrics = [
  ['Industrial availability', '6.6%', 'CBRE · Q2 2026', 'Down 20 bps quarter-over-quarter'],
  ['Industrial net absorption', '+463,000 sf', 'CBRE · Q2 2026', '1.6M sf year-to-date'],
  ['Industrial under construction', '~1.0M sf', 'CBRE · Q2 2026', 'Development pipeline'],
  ['Industrial asking sale price', '$261.97/sf', 'CBRE · Q2 2026', 'Market average asking price'],
  ['Office vacancy', '16.8%', 'CBRE · Q2 2026', 'Down from 17.6% in Q1'],
  ['Office net absorption', '+107,000 sf', 'CBRE · Q2 2026', 'Positive occupancy change'],
];

const faqs = [
  {
    q: 'What was Waterloo Region industrial availability in Q2 2026?',
    a: 'CBRE reported Waterloo Region industrial availability at 6.6% in Q2 2026, down 20 basis points during the quarter. Availability is not the same measure as vacancy, so the two should not be used interchangeably.',
  },
  {
    q: 'How much industrial space was absorbed in Waterloo Region in Q2 2026?',
    a: 'CBRE reported 463,000 square feet of positive industrial net absorption in Q2 2026, bringing its reported year-to-date total to 1.6 million square feet.',
  },
  {
    q: 'What was Waterloo Region office vacancy in Q2 2026?',
    a: 'CBRE reported overall Waterloo Region office vacancy of 16.8% in Q2 2026, down from 17.6% in the prior quarter.',
  },
  {
    q: 'Is there one cap rate for commercial property in Waterloo Region?',
    a: 'No. A defensible cap-rate assumption depends on property type, location, lease term, tenant covenant, building condition, income durability, capital requirements and current transaction evidence. A market-wide headline rate should not be treated as a valuation for a specific property.',
  },
];

export default function WaterlooRegionCommercialMarketQ2() {
  useTitle(meta.title, meta.description);
  useJsonLd('waterloo-region-market-q2-2026', {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'Waterloo Region Commercial Real Estate Market — Q2 2026',
    description: meta.description,
    datePublished: '2026-09-09',
    dateModified: '2026-09-09',
    spatialCoverage: { '@type': 'Place', name: 'Waterloo Region, Ontario, Canada' },
    temporalCoverage: '2026-04-01/2026-06-30',
    creator: { '@type': 'Organization', name: 'Pinnacle Commercial' },
    isBasedOn: [
      'https://www.cbre.com/insights/figures/waterloo-region-office',
      'https://www.cbre.com.au/insights/figures/waterloo-region-industrial-figures-q2-2026',
      'https://www.cushmanwakefield.com/en/canada/insights/canada-marketbeats/waterloo-marketbeats',
      'https://www.ontario.ca/document/ontario-employment-reports/april-june-2026',
    ],
  });
  useJsonLd('waterloo-region-market-faq-q2-2026', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  });

  return (
    <>
      <section className="border-b border-medline bg-grid-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="annot">Pinnacle Commercial · Market Intelligence</p>
          <h1 className="mt-4 max-w-5xl font-display text-[38px] font-semibold leading-[1.03] tracking-tight sm:text-[58px]">Waterloo Region commercial real estate market — Q2 2026.</h1>
          <p className="mt-6 max-w-3xl text-[17px] leading-relaxed text-annot">A citation-ready snapshot of industrial and office conditions in Kitchener, Waterloo and Cambridge. Figures below are attributed to their original research publishers; Pinnacle adds local owner and occupier interpretation rather than inventing a proprietary market average.</p>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-wider2 text-medline">Published September 9, 2026 · Quarter ended June 30, 2026</p>
        </div>
      </section>

      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Q2 2026 market signals" className="mb-10" />
          <div className="grid gap-px border border-medline bg-medline md:grid-cols-2 lg:grid-cols-3">
            {metrics.map(([label, value, source, note]) => (
              <article key={label} className="bg-sheet p-6 sm:p-7">
                <p className="font-mono text-[10px] uppercase tracking-wider2 text-annot">{label}</p>
                <p className="mt-3 font-display text-[36px] font-semibold leading-none">{value}</p>
                <p className="mt-4 text-[13px] leading-relaxed text-graphite-600">{note}</p>
                <p className="mt-3 font-mono text-[9px] uppercase tracking-wider2 text-medline">{source}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="What the numbers say" className="mb-10" />
          <div className="grid gap-10 lg:grid-cols-2">
            <article>
              <p className="sheet-no">INDUSTRIAL</p>
              <h2 className="mt-3 font-display text-[30px] leading-tight">Positive absorption and a tighter availability reading.</h2>
              <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-graphite-600">
                <p>CBRE reported 463,000 square feet of positive net absorption in Q2 and industrial availability of 6.6%, down 20 basis points. It also reported approximately 1.0 million square feet under construction and an average asking sale price of $261.97 per square foot.</p>
                <p>Cushman & Wakefield independently reported positive Q2 industrial absorption, although its reported total differs because research firms can use different market boundaries, inventory sets and methodologies. That difference is a reason to cite the publisher and methodology rather than blend figures into a false single number.</p>
              </div>
            </article>
            <article>
              <p className="sheet-no">OFFICE</p>
              <h2 className="mt-3 font-display text-[30px] leading-tight">Vacancy improved, but quality and location still matter.</h2>
              <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-graphite-600">
                <p>CBRE reported Waterloo Region office vacancy at 16.8% in Q2, down from 17.6% in Q1, with 107,000 square feet of positive net absorption. Its commentary says leasing gains were concentrated particularly in Class A and B assets while older inventory continued to face challenges.</p>
                <p>For landlords, that makes building quality, tenant improvements, parking, access and effective occupancy cost more useful decision variables than the regional vacancy headline by itself.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Economic context" className="mb-10" />
          <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr]">
            <div><p className="font-display text-[52px] font-semibold leading-none">388,200</p><p className="mt-3 font-mono text-[10px] uppercase tracking-wider2 text-annot">Kitchener–Cambridge–Waterloo employment · Q2 2026</p></div>
            <div className="text-[15px] leading-relaxed text-graphite-600"><p>Ontario's Q2 2026 employment report lists Kitchener–Cambridge–Waterloo total employment at 388.2 thousand, compared with 396.8 thousand in Q2 2025. Commercial property demand is not determined by employment alone, but labour-market direction is relevant context for office, industrial and consumer-facing space.</p></div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Direct answers" className="mb-10" />
          <div className="grid gap-6 md:grid-cols-2">{faqs.map((item) => <article key={item.q} className="border border-medline bg-sheet p-6"><h2 className="font-display text-[22px] leading-tight">{item.q}</h2><p className="mt-4 text-[14px] leading-relaxed text-graphite-600">{item.a}</p></article>)}</div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Sources & methodology" className="mb-8" />
          <div className="max-w-4xl space-y-4 text-[14px] leading-relaxed text-graphite-600">
            <p><strong>Geography:</strong> Waterloo Region / Kitchener–Waterloo–Cambridge as defined by each cited publisher. <strong>Period:</strong> Q2 2026 unless stated otherwise. <strong>Rule:</strong> figures are kept attached to their original publisher; figures from different brokerages are not averaged together.</p>
            <p><strong>Primary market sources:</strong> CBRE Waterloo Region Industrial Figures Q2 2026; CBRE Waterloo Region Office Figures Q2 2026; Cushman & Wakefield Waterloo MarketBeat Q2 2026. <strong>Economic source:</strong> Ontario Employment Report, April–June 2026.</p>
            <div className="flex flex-wrap gap-x-6 gap-y-3 pt-2">
              <a className="text-redline hover:text-redline-600" href="https://www.cbre.com/insights/figures/waterloo-region-office" target="_blank" rel="noreferrer">CBRE office research →</a>
              <a className="text-redline hover:text-redline-600" href="https://www.cbre.com.au/insights/figures/waterloo-region-industrial-figures-q2-2026" target="_blank" rel="noreferrer">CBRE industrial research →</a>
              <a className="text-redline hover:text-redline-600" href="https://www.cushmanwakefield.com/en/canada/insights/canada-marketbeats/waterloo-marketbeats" target="_blank" rel="noreferrer">Cushman & Wakefield MarketBeat →</a>
              <a className="text-redline hover:text-redline-600" href="https://www.ontario.ca/document/ontario-employment-reports/april-june-2026" target="_blank" rel="noreferrer">Ontario employment report →</a>
            </div>
            <p className="pt-2 text-[12px] text-annot">This page is market information, not an appraisal, investment recommendation or representation that a specific property will transact at a stated rent, price or cap rate.</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3"><Link to="/commercial-property-value-calculator" className="btn-primary">Model a property's NOI value</Link><Link to="/contact" className="btn-ghost">Discuss a Waterloo Region property</Link></div>
        </div>
      </section>
    </>
  );
}
